/**
 * TODO1 - the to-do screens' shared words and dates. ⭐ Every date and time shown is PACIFIC, whatever the device's own
 * zone: "today", "overdue" and "until Tue 7:30 AM" are the business's, and the server decides them the same way.
 */
const PACIFIC = 'America/Los_Angeles';

/** ⭐ R14: GoHighLevel closes a task only through its contact, so a contactless one is closed there - said, not hidden. */
export const NO_CONTACT = 'No contact in GoHighLevel - close this one there.';
/** His word: a task with nobody assigned goes on both lists. */
export const SHARED = 'Unassigned - on both lists';

/** The Pacific date as YYYY-MM-DD. ⛔ `en-CA` is for its shape (it sorts as the dates do), not for a country. */
export const pacificDate = (at: Date): string => at.toLocaleDateString('en-CA', { timeZone: PACIFIC });

const pacificYear = (at: Date): string => pacificDate(at).slice(0, 4);

/**
 * "Oct 15" - and "Oct 8, 2027" outside this Pacific year. ⛔ A date with its year left off reads as this year's: the 148th
 * look found 78 of 247 to-dos due in 2027 showing "Due Oct 8", which read as nine days ago.
 */
export const dueDay = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  return at.toLocaleDateString('en-US', {
    timeZone: PACIFIC, month: 'short', day: 'numeric', ...(pacificYear(at) === pacificYear(now) ? {} : { year: 'numeric' }),
  });
};

/** By the Pacific DATE: a task due later today is due, not overdue. */
export const isOverdue = (dueAt: string | null, now: Date): boolean =>
  dueAt !== null && pacificDate(new Date(dueAt)) < pacificDate(now);

/** Due today or overdue, by the Pacific DATE - the reminder's test (the server's `dueTodayOrOverdue`). */
export const isDueByToday = (dueAt: string | null, now: Date): boolean =>
  dueAt !== null && pacificDate(new Date(dueAt)) <= pacificDate(now);

/**
 * ⭐ R50 (his pick, 2026-10-17) - the order the reminder names to-dos in, so the check-in names the SAME five in the SAME
 * order: due TODAY (Pacific) first, earliest due time first; then the OVERDUE ones, the most RECENTLY due first; then the
 * ones due later, soonest first; no due date last. Ties by task id.
 * ⛔ The server's own is todoRules.sortByUrgency - this cannot import it (a frontend file never imports backend source),
 * so CheckIn.test.tsx and todoRules.test.ts hold one fixture and expect one order. Change both or neither.
 */
export const sortByUrgency = <T extends { ghl_task_id: string; due_at: string | null }>(rows: T[], now: Date): T[] => {
  const today = pacificDate(now);
  const keyed = rows.map((t) => {
    const ms = t.due_at ? Date.parse(t.due_at) : NaN;
    if (!Number.isFinite(ms)) return { t, group: 3, ms: 0 };
    const day = pacificDate(new Date(ms));
    return { t, group: day === today ? 0 : day < today ? 1 : 2, ms };
  });
  keyed.sort((a, b) => {
    if (a.group !== b.group) return a.group - b.group;
    if (a.ms !== b.ms) return a.group === 1 ? b.ms - a.ms : a.ms - b.ms;
    return a.t.ghl_task_id < b.t.ghl_task_id ? -1 : a.t.ghl_task_id > b.t.ghl_task_id ? 1 : 0;
  });
  return keyed.map((k) => k.t);
};

/**
 * "until Tue 7:30 AM" within the coming week; further out "until Tue, Nov 17, 7:30 AM" (and the year outside this one) -
 * a weekday alone could be any Tuesday, and Pick a date reaches any date.
 */
export const untilLabel = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  // Pacific DATES, each read as a UTC midnight, so a DST day of 23 or 25 hours never changes the count.
  const days = (Date.parse(pacificDate(at)) - Date.parse(pacificDate(now))) / 86_400_000;
  const clock = { timeZone: PACIFIC, weekday: 'short', hour: 'numeric', minute: '2-digit' } as const;
  if (days >= 0 && days < 7) return `until ${at.toLocaleString('en-US', clock)}`;
  const year = pacificYear(at) === pacificYear(now) ? {} : { year: 'numeric' as const };
  return `until ${at.toLocaleString('en-US', { ...clock, month: 'short', day: 'numeric', ...year })}`;
};

const CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' });

/**
 * ⭐ R11: "Later today" skips to the next reminder today, and from 3:30 PM Pacific there is none - the server refuses
 * it then with its own words. This only decides whether the choice is OFFERED; the server picks the time.
 */
export const laterTodayOffered = (now: Date): boolean => {
  const part = (type: string) => Number(CLOCK.formatToParts(now).find((p) => p.type === type)?.value);
  return part('hour') * 60 + part('minute') < 15 * 60 + 30;
};

/** The first day "Pick a date" may take: the server snoozes to days after today only. */
export const pacificTomorrow = (now: Date): string => {
  const [y, m, d] = pacificDate(now).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

const WATCHDOG_CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC, hour: 'numeric', minute: '2-digit', hour12: true });

/** The Pacific date a day before - by the calendar, so a DST day of 23 or 25 hours never skips one. */
const pacificYesterday = (now: Date): string => {
  const [y, m, d] = pacificDate(now).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
};

/**
 * The Call Intelligence watchdog's banner (Task 10) - the server's push says the same words (todoRules.watchdogLine).
 * ⭐ The time from its parts: newer browsers put a narrow no-break space before "AM".
 * ⭐ R53: a call from an earlier Pacific day names it - "since yesterday 2:15 PM", "since Oct 15, 2:15 PM" - or a morning's
 * banner saying "since 2:15 PM" reads as this afternoon.
 */
export const watchdogLine = (missing: number, since: string, now: Date): string => {
  const at = new Date(since);
  const p = Object.fromEntries(WATCHDOG_CLOCK.formatToParts(at).map((x) => [x.type, x.value]));
  const clock = `${p.hour}:${p.minute} ${p.dayPeriod}`;
  const day = pacificDate(at);
  const when = day === pacificDate(now) ? clock : day === pacificYesterday(now) ? `yesterday ${clock}` : `${dueDay(since, now)}, ${clock}`;
  const calls = missing === 1 ? '1 call' : `${missing} calls`;
  return `${calls} since ${when} ${missing === 1 ? 'has' : 'have'} no summary yet - the Mac Mini may be off.`;
};

/** The server's words when it sent some; ours when it did not. */
export const wordsOf = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

export type TodoGroupKey = 'today' | 'recent' | 'upcoming' | 'old' | 'none';
export interface TodoGroup<T> { key: TodoGroupKey; label: string; rows: T[]; startsCollapsed: boolean }
const GROUP_ORDER: { key: TodoGroupKey; label: string; startsCollapsed: boolean }[] = [
  { key: 'today', label: 'Due today', startsCollapsed: false },
  { key: 'recent', label: 'Overdue, last 7 days', startsCollapsed: false },
  { key: 'upcoming', label: 'Coming up', startsCollapsed: false },
  // The pile: most of a long list (the 156th: ~190 of ~440) is more than a week overdue. It is one tap away and its label
  // says how many it holds - a control must name its contents, or a closed group reads as the to-dos having gone.
  { key: 'old', label: 'Overdue more than a week', startsCollapsed: true },
  { key: 'none', label: 'No due date', startsCollapsed: false },
];

/**
 * His open list in day groups (his word, 2026-10-24: "a whole bunch ... a better way to organize them"). By the Pacific DATE,
 * never the instant. Inside a group the order is R50's (`sortByUrgency`), so the first rows are the same ones a reminder
 * names. Empty groups are dropped.
 */
export const groupTodos = <T extends { ghl_task_id: string; due_at: string | null }>(rows: T[], now: Date): TodoGroup<T>[] => {
  const today = pacificDate(now);
  const weekAgo = new Date(Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10) - 7)).toISOString().slice(0, 10);
  const keyOf = (t: T): TodoGroupKey => {
    if (!t.due_at || !Number.isFinite(Date.parse(t.due_at))) return 'none';
    const day = pacificDate(new Date(t.due_at));
    if (day === today) return 'today';
    if (day > today) return 'upcoming';
    return day >= weekAgo ? 'recent' : 'old';
  };
  const ordered = sortByUrgency(rows, now);
  return GROUP_ORDER
    .map((g) => ({ ...g, rows: ordered.filter((t) => keyOf(t) === g.key) }))
    .filter((g) => g.rows.length > 0);
};

export type TodoKind = 'call' | 'message' | 'reengage' | 'other';
/**
 * A re-engage / outreach nudge, a call, a text / email, or the rest - by the WORD in the title ("Recall" is not "call").
 * A title naming both a call and a text is a call.
 */
export const kindOf = (title: string): TodoKind => {
  if (/^\s*(re-?engage|outreach)\b/i.test(title)) return 'reengage';
  if (/\bcall\b/i.test(title)) return 'call';
  if (/\b(text|email|e-mail|message|send|reply)\b/i.test(title)) return 'message';
  return 'other';
};

/** The search box: the title or the contact, any case. An empty search keeps everything. */
export const matchesQuery = (t: { title: string; contact_name: string | null }, query: string): boolean => {
  const q = query.trim().toLowerCase();
  return q === '' || t.title.toLowerCase().includes(q) || (t.contact_name ?? '').toLowerCase().includes(q);
};

/**
 * MORNING1 - how long a text or a missed call has waited: "just now", "30 minutes ago", "5 hours ago", "yesterday",
 * "3 days ago", then the date. ⭐ "yesterday" and the days are by the PACIFIC date, so 8 PM last night is yesterday even
 * where UTC calls it today. A time a little in the future (two clocks apart) is "just now", never a negative count.
 */
export const agoLabel = (iso: string, now: Date = new Date()): string => {
  const minutes = Math.floor((now.getTime() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const day = pacificDate(new Date(iso));
  if (day === pacificDate(now)) {
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (day === pacificYesterday(now)) return 'yesterday';
  // Pacific DATES, each read as a UTC midnight, so a DST day never changes the count.
  const days = (Date.parse(pacificDate(now)) - Date.parse(day)) / 86_400_000;
  return days < 7 ? `${days} days ago` : dueDay(iso, now);
};

/** How long overdue a deal deadline may be and still sit in view on the Morning page (his word, 2026-10-26). */
export const DEADLINE_OLD_DAYS = 30;
/**
 * Deal deadlines split by the Pacific date: more than DEADLINE_OLD_DAYS overdue goes behind one tap ("Older overdue
 * deadlines (N)") - his words: hide them, "but make it so I can get to them with 1 tap". Order kept.
 */
export const splitDeadlines = <T extends { due_date: string }>(items: T[], now: Date): { recent: T[]; older: T[] } => {
  const [y, m, d] = pacificDate(now).split('-').map(Number);
  const floor = new Date(Date.UTC(y, m - 1, d - DEADLINE_OLD_DAYS)).toISOString().slice(0, 10);
  return { recent: items.filter((x) => x.due_date.slice(0, 10) >= floor), older: items.filter((x) => x.due_date.slice(0, 10) < floor) };
};

/**
 * The home-screen app starts on the Morning page - "but only in the morning" (his words, 2026-10-26): the FIRST open of
 * a Pacific day between 5 AM and noon, and only from the installed app (a browser tab keeps its usual start page).
 * `lastMorningDay` is the Pacific date this last happened (kept per device).
 */
export const opensOnMorning = ({ now, standalone, lastMorningDay }: { now: Date; standalone: boolean; lastMorningDay: string | null }): boolean => {
  if (!standalone) return false;
  const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC, hourCycle: 'h23', hour: '2-digit' }).format(now));
  return hour >= 5 && hour < 12 && lastMorningDay !== pacificDate(now);
};
